import { useDispatch } from 'react-redux';

import {
    type PendingUserSlice,
    clearPendingUser,
    setPendingUser,
    updatePendingUserProps
} from './pendingUserSlice';
import { setApiResponseStatus } from '../../common/application/apiResponseStatusSlice';
import { handleHttpError } from '../../common/application/handleHttpError';
import { toggleToast } from '../../common/application/toastSlice';

import { setUser } from '@/auth/application/authSlice';
import type User from '@/auth/domain/auth';
import { RequestTypeEnum } from '@/common/domain/toast';
import { logException, logTrace } from '@/common/utils/logger';
import useUserApi from '@/user/data/userApi';


export const useUser = () => {
    const dispatch = useDispatch();
    const { apiGetUserProfile, apiUpdateUser, apiDeleteUserAccount } = useUserApi();

    const getUserProfile = async () => {
        try {
            const response = await apiGetUserProfile();
            handleHttpError(response.status);
            dispatch(setUser(response.data));
            logTrace('User profile fetched', undefined, { id: response.data.id });
            dispatch(setPendingUser(response.data));
            dispatch(setApiResponseStatus(response.status)); 
            return response;
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.getUserProfile, text: error.response?.data?.error?.message }));
            }
        }
    };

    // Delete current user account
    const deleteUserAccount = async () => {
        try {
            const response = await apiDeleteUserAccount();
            handleHttpError(response.status);
            dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.deleteUser}));
            dispatch(setUser(response.data));
            return response.status;
        } catch (error: any) {
            logException(error);
            if (error?.status) {
                dispatch(toggleToast({ code: error.status, requestType: RequestTypeEnum.deleteUser, text: error?.message }));
            }

            throw error;
        }
    };

    const updateUser = async (pendingUser: User) => {
        try {
            const response = await apiUpdateUser(pendingUser);
            handleHttpError(response.status);
            dispatch(toggleToast({...response.status, requestType: RequestTypeEnum.updateUser}));
            dispatch(setUser(response.data));
            return response.status;
        } catch (error: any) {
            if (error?.status) {
                dispatch(toggleToast({ code: error.response?.status, requestType: RequestTypeEnum.updateUser, text: error.response?.data?.error?.message }));
            }
        }
    };

    const clearPendingUserCont = async () => {
        dispatch(clearPendingUser());
    };

    const setPendingUserCont = (pendingUser: PendingUserSlice) => {
        dispatch(setPendingUser(pendingUser));
    };

    const updatePendingUserPropsCont = (field: string, value: any) => {
        dispatch(updatePendingUserProps({ field, value }));
    };
    return {
        clearPendingUserCont,
        deleteUserAccount,
        getUserProfile,
        setPendingUserCont,
        updatePendingUserPropsCont,
        updateUser
    };
};

